import { useState, useCallback, useMemo, createContext, useContext } from 'react'
import { setDevRole } from './useApi.js'

export type Role = 'AGENT' | 'AGENCY_ADMIN' | 'COMPLIANCE_OFFICER' | 'COLLABORATOR' | 'CLIENT' | 'LOCATAIRE' | 'OWNER' | 'SYNDIC' | 'NOTAIRE' | 'SUPPORT' | 'PLATFORM_ADMIN'

// ── Permission types (mirrored from server middleware/permissions.ts) ──

export type Resource =
  | 'contacts'
  | 'mandats'
  | 'transactions'
  | 'visites'
  | 'pipeline'
  | 'mailbox'
  | 'documents'
  | 'aria'
  | 'admin'
  | 'settings'
  | 'billing'
  | 'publication'
  | 'calendar'
  | 'tasks'
  | 'platform'
  | 'aml'
  | 'aml-review'
  | 'aml-ctif'
  | 'aml-procedure'
  | 'aml-registre'

export type Action = 'create' | 'read' | 'update' | 'delete' | 'own_only'

// ── Permission matrix (same as server — single source of truth pattern) ──

const ROLE_PERMISSIONS: Record<string, Partial<Record<Resource, Action[]>>> = {
  COLLABORATOR: {
    contacts: ['create', 'read', 'update', 'delete', 'own_only'],
    mandats: ['read'],
    transactions: ['read'],
    visites: ['create', 'read', 'update', 'delete'],
    pipeline: ['read'],
    mailbox: ['read', 'own_only'],
    documents: ['read', 'create'],
    aria: ['read'],
    calendar: ['create', 'read', 'update', 'delete'],
    tasks: ['create', 'read', 'update', 'delete'],
    aml: [], 'aml-review': [], 'aml-ctif': [], 'aml-procedure': [], 'aml-registre': [],
    admin: [],
    settings: [],
    billing: [],
    platform: [],
  },
  AGENT: {
    contacts: ['create', 'read', 'update', 'delete'],
    mandats: ['create', 'read', 'update', 'delete'],
    transactions: ['create', 'read', 'update', 'delete'],
    visites: ['create', 'read', 'update', 'delete'],
    pipeline: ['create', 'read', 'update', 'delete'],
    mailbox: ['create', 'read', 'update', 'delete'],
    documents: ['create', 'read', 'update', 'delete'],
    aria: ['create', 'read', 'update', 'delete'],
    calendar: ['create', 'read', 'update', 'delete'],
    tasks: ['create', 'read', 'update', 'delete'],
    publication: ['create', 'read', 'update', 'delete'],
    aml: ['create', 'read', 'update', 'own_only'],
    'aml-review': ['read'],
    'aml-ctif': [], 'aml-procedure': ['read'], 'aml-registre': [],
    settings: ['read', 'update'],
    admin: [],
    billing: [],
    platform: [],
  },
  AGENCY_ADMIN: {
    contacts: ['create', 'read', 'update', 'delete'],
    mandats: ['create', 'read', 'update', 'delete'],
    transactions: ['create', 'read', 'update', 'delete'],
    visites: ['create', 'read', 'update', 'delete'],
    pipeline: ['create', 'read', 'update', 'delete'],
    mailbox: ['create', 'read', 'update', 'delete'],
    documents: ['create', 'read', 'update', 'delete'],
    aria: ['create', 'read', 'update', 'delete'],
    calendar: ['create', 'read', 'update', 'delete'],
    tasks: ['create', 'read', 'update', 'delete'],
    publication: ['create', 'read', 'update', 'delete'],
    aml: ['create', 'read', 'update', 'delete'],
    'aml-review': ['create', 'read', 'update'],
    'aml-ctif': ['create', 'read'],
    'aml-procedure': ['create', 'read', 'update'],
    'aml-registre': ['read'],
    admin: ['create', 'read', 'update', 'delete'],
    settings: ['create', 'read', 'update', 'delete'],
    billing: ['create', 'read', 'update', 'delete'],
    platform: [],
  },
  COMPLIANCE_OFFICER: {
    contacts: ['create', 'read', 'update'],
    mandats: ['create', 'read', 'update'],
    transactions: ['create', 'read', 'update'],
    visites: ['create', 'read', 'update'],
    pipeline: ['read'],
    mailbox: ['create', 'read', 'update', 'own_only'],
    documents: ['create', 'read', 'update'],
    aria: ['create', 'read'],
    calendar: ['create', 'read', 'update', 'delete'],
    tasks: ['create', 'read', 'update', 'delete'],
    publication: ['read'],
    aml: ['create', 'read', 'update'],
    'aml-review': ['create', 'read', 'update'],
    'aml-ctif': ['create', 'read'],
    'aml-procedure': ['create', 'read', 'update'],
    'aml-registre': ['read'],
    admin: [], settings: ['read'], billing: [], platform: [],
  },
  CLIENT: {
    contacts: [],
    mandats: ['read'],
    transactions: ['read'],
    documents: ['read'],
    aml: [], 'aml-review': [], 'aml-ctif': [], 'aml-procedure': [], 'aml-registre': [],
    admin: [],
    settings: [],
    billing: [],
    platform: [],
  },
  SUPPORT: {
    contacts: ['read'],
    mandats: ['read'],
    transactions: ['read'],
    visites: ['read'],
    pipeline: ['read'],
    mailbox: ['read'],
    documents: ['read'],
    aria: ['read'],
    calendar: ['read'],
    tasks: ['read'],
    publication: ['read'],
    aml: ['read'], 'aml-review': ['read'], 'aml-ctif': ['read'], 'aml-procedure': ['read'], 'aml-registre': ['read'],
    admin: ['read'],
    settings: ['read'],
    billing: ['read'],
    platform: [],
  },
  PLATFORM_ADMIN: {
    contacts: ['create', 'read', 'update', 'delete'],
    mandats: ['create', 'read', 'update', 'delete'],
    transactions: ['create', 'read', 'update', 'delete'],
    visites: ['create', 'read', 'update', 'delete'],
    pipeline: ['create', 'read', 'update', 'delete'],
    mailbox: ['create', 'read', 'update', 'delete'],
    documents: ['create', 'read', 'update', 'delete'],
    aria: ['create', 'read', 'update', 'delete'],
    calendar: ['create', 'read', 'update', 'delete'],
    tasks: ['create', 'read', 'update', 'delete'],
    publication: ['create', 'read', 'update', 'delete'],
    aml: ['create', 'read', 'update', 'delete'],
    'aml-review': ['create', 'read', 'update', 'delete'],
    'aml-ctif': ['create', 'read', 'update', 'delete'],
    'aml-procedure': ['create', 'read', 'update', 'delete'],
    'aml-registre': ['create', 'read', 'update', 'delete'],
    admin: ['create', 'read', 'update', 'delete'],
    settings: ['create', 'read', 'update', 'delete'],
    billing: ['create', 'read', 'update', 'delete'],
    platform: ['create', 'read', 'update', 'delete'],
  },
}

// ── Context ──

interface RoleCtx {
  role: Role
  setRole: (r: Role) => void
  can: (resource: Resource, action: Action) => boolean
  isSupport: boolean
  isPlatformAdmin: boolean
}

export const RoleContext = createContext<RoleCtx>({
  role: 'AGENT',
  setRole: () => {},
  can: () => false,
  isSupport: false,
  isPlatformAdmin: false,
})

export function useRole() {
  return useContext(RoleContext)
}

export function useRoleProvider() {
  const [role, _setRole] = useState<Role>('AGENT')

  const setRole = useCallback((r: Role) => {
    _setRole(r)
    // Sync with API layer so backend impersonates the right user
    setDevRole(r)
  }, [])

  const can = useCallback((resource: Resource, action: Action): boolean => {
    const perms = ROLE_PERMISSIONS[role]?.[resource]
    return !!perms && perms.includes(action)
  }, [role])

  const isSupport = role === 'SUPPORT'
  const isPlatformAdmin = role === 'PLATFORM_ADMIN'

  const value = useMemo(() => ({
    role,
    setRole,
    can,
    isSupport,
    isPlatformAdmin,
  }), [role, setRole, can, isSupport, isPlatformAdmin])

  return value
}
